/**
 * DecisionJournal — makes a culling decision durable the instant it is made.
 *
 * The SQLite snapshot is written asynchronously and debounced, which is right
 * for throughput but means the last moment of work is in memory only. A tab
 * closed or reloaded in that window used to lose it, and `pagehide` cannot help:
 * the write is async and the page is gone before it lands.
 *
 * localStorage is synchronous, so appending here returns only once the decision
 * is actually stored. On the next boot the journal is replayed into the database
 * and cleared. Entries are idempotent — "media 42 goes in bucket 1" — so
 * replaying one that already made it into the snapshot changes nothing.
 *
 * This holds only decisions, never photographs.
 */

const KEY = 'm3cull.journal.v1';
/** Well past a debounce window; the journal is drained on every boot anyway. */
const MAX_ENTRIES = 5000;

export type JournalEntry =
  | { t: 'bucket'; id: number; v: number | null }
  | { t: 'rotation'; id: number; v: number };

export class DecisionJournal {
  private buffer: JournalEntry[] = [];

  constructor() {
    this.buffer = this.read();
  }

  /** Synchronous. Returns once the decision is stored. */
  append(entry: JournalEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length > MAX_ENTRIES) {
      this.buffer = this.buffer.slice(-MAX_ENTRIES);
    }
    this.write();
  }

  /** Everything recorded since the last clear. */
  drain(): JournalEntry[] {
    return [...this.buffer];
  }

  clear(): void {
    this.buffer = [];
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* private mode, or storage disabled — nothing to clean up */
    }
  }

  private read(): JournalEntry[] {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as JournalEntry[]) : [];
    } catch {
      return []; // unreadable or unavailable; the snapshot is still authoritative
    }
  }

  private write(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.buffer));
    } catch {
      // Quota or private mode. The debounced snapshot remains the safety net,
      // so failing here degrades durability rather than breaking culling.
    }
  }
}

/** Used where journalling is not wanted, such as tests. */
export class NoopJournal {
  append(): void {}
  drain(): JournalEntry[] {
    return [];
  }
  clear(): void {}
}
